import React, { useEffect, useState } from "react";
import * as platformService from "../../../services/platformService";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import { EmptyState, FormDrawer, LoadState, PrimaryButton, cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../../../components/ui";

// How documents are numbered (quotations, orders, invoices ...): a pattern per document
// type for the whole company, optionally a different one for a business unit or a
// location. Numbers are gapless; a changed pattern applies to the next document.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const SCOPES = [
  { label: "Whole company", value: "ALL" },
  { label: "One business unit", value: "BU" },
  { label: "One location", value: "LOCATION" },
];
const RESETS = [
  { label: "Never (one running number)", value: "NEVER" },
  { label: "Every calendar year", value: "YEARLY" },
  { label: "Every month", value: "MONTHLY" },
  { label: "Every fiscal year", value: "FISCAL" },
];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((label, i) => ({ label, value: i + 1 }));
const TOKENS_HINT = "Tokens: {SEQ} the number (required) · {YYYY} {YY} {MM} the date · {FY} fiscal year · {LOC} location code · {BU} business unit code";

function Preview({ module, values }) {
  const [example, setExample] = useState(null);
  useEffect(() => {
    if (!values.doc_type || !values.pattern) return setExample(null);
    let alive = true;
    const t = setTimeout(async () => {
      const res = await platformService.previewDocSequence(module, { ...values, scope_type: "ALL", scope_id: undefined }).catch(() => null);
      if (alive) setExample(res?.success ? res.data.example : res?.message || null);
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [module, values]);
  if (!example) return null;
  return (
    <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
      Next number will look like: <strong>{example}</strong>
    </div>
  );
}

// wholeCompanyOnly: business units and locations belong to Finance, so Procurement numbers the whole company.
export default function NumberingSetup({ module, wholeCompanyOnly = false }) {
  const { data, loading, error, reload } = useLoad(() => platformService.listDocSequences(module), [module]);
  const opts = useFinOptions(["business-units", "locations"]);
  const [edit, setEdit] = useState(undefined);
  const docTypes = (data?.doc_types || []).map((d) => ({ label: d.label, value: d.value }));
  const rows = data?.sequences || [];

  const toForm = (r) => ({
    doc_type: r.doc_type,
    scope_type: r.scope_type,
    scope_id: r.scope_type === "BU" ? optionByCode(opts["business-units"], r.scope_code) : r.scope_type === "LOCATION" ? optionByCode(opts.locations, r.scope_code) : null,
    pattern: r.pattern,
    padding: r.padding,
    start_value: r.start_value,
    reset_policy: r.reset_policy,
    fy_start_month: r.fy_start_month,
  });

  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add numbering</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No numbering yet" text="Each document type needs a numbering pattern before its first document." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Document", "For", "Pattern", "Digits", "Restarts", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{r.doc_type_label}</td>
                    <td style={tdStyle}>{r.scope_code ? `${r.scope_code} – ${r.scope_name}` : r.scope_name}</td>
                    <td style={{ ...tdStyle, fontFamily: "monospace" }}>{r.pattern}</td>
                    <td style={tdStyle}>{r.padding || "—"}</td>
                    <td style={tdStyle}>{(RESETS.find((x) => x.value === r.reset_policy) || {}).label}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(r)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit numbering" : "Add numbering"}
        initial={edit ? toForm(edit) : { scope_type: "ALL", padding: 0, start_value: 1, reset_policy: "NEVER", fy_start_month: 1 }}
        onHide={() => setEdit(undefined)}
        onSave={(clean) => platformService.saveDocSequence(module, clean)}
        onDone={reload}
        fields={(v) => [
          { key: "doc_type", label: "Document", type: "select", required: true, options: docTypes, disabled: !!edit },
          { key: "scope_type", label: "Applies to", type: "select", required: true, options: wholeCompanyOnly ? SCOPES.slice(0, 1) : SCOPES, disabled: !!edit },
          ...(v.scope_type === "BU" ? [{ key: "scope_id", label: "Business unit", type: "select", required: true, options: opts["business-units"] || [], disabled: !!edit }] : []),
          ...(v.scope_type === "LOCATION" ? [{ key: "scope_id", label: "Location", type: "select", required: true, options: opts.locations || [], disabled: !!edit }] : []),
          { key: "pattern", label: "Pattern", required: true, max: 64, hint: TOKENS_HINT },
          { key: "padding", label: "Digits (pad with zeros, 0 = none)", type: "number", min: 0 },
          { key: "start_value", label: "First number", type: "number", min: 1 },
          { key: "reset_policy", label: "Restart numbering", type: "select", options: RESETS },
          ...(v.reset_policy === "FISCAL" || /\{FY\}/.test(v.pattern || "") ? [{ key: "fy_start_month", label: "Fiscal year starts in", type: "select", options: MONTHS }] : []),
        ]}
      >
        {(values) => <Preview module={module} values={values} />}
      </FormDrawer>
    </>
  );
}
